<template>
  <div>
    <pdl-button class="text-left underline" text @click="onLinkButtonClick">{{ title }}</pdl-button>
    <ref-link-dialog
      :external-link="externalLink"
      :is-dialog-visible="isDialogVisible"
      :link-target="linkTarget"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import storefrontInstance from '@/api/instances/storefront';
import RefLinkDialog from '@/components/ref-link/RefLinkDialog';
import {PdlButton} from '@pedal/pdl-button';
import {mapGetters, mapState} from 'vuex';
import {ProductApi} from '@/api/product';

export default {
  name: 'RefLink',
  components: {RefLinkDialog, PdlButton},
  props: {
    title: {
      type: String,
      required: true,
    },
    link: {
      type: String,
      required: true,
    },
    assetType: {
      type: String,
      default: undefined,
    },
    assetLocation: {
      type: String,
      default: undefined,
    },
    target: {
      type: String,
      default: undefined,
    },
    reportName: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      isDialogVisible: false,
      externalLink: '',
      dynamicHref: '',
    };
  },
  computed: {
    ...mapGetters('viewSize', ['viewBreakpointLessThan']),
    ...mapState('backend', ['kioskModeEnabledOnSite']),

    isAzureLink() {
      return this.assetLocation === 'Azure';
    },
    isCloudinaryLink() {
      return this.assetLocation === 'Cloudinary';
    },
    isPlainLink() {
      return !this.isAzureLink && !this.isCloudinaryLink;
    },
    isContent() {
      return this.assetType === 'content';
    },
    linkTarget() {
      return this.opensInNewWindow ? '_blank' : '_self';
    },
    opensInNewWindow() {
      return this.target?.toUpperCase() === 'NEWWINDOW' && !this.kioskModeEnabledOnSite;
    },
    hasExternalLinkAtBreakpoint() {
      return this.externalLink && this.viewBreakpointLessThan('large');
    },
  },
  methods: {
    async processLink() {
      if (this.isAzureLink) {
        if (this.reportName) {
          return this.openGptReportLink();
        }
        await this.openLink();
      }

      if (this.isCloudinaryLink) {
        this.openCloudinaryLink();
      }
      if (this.isPlainLink) {
        this.dynamicHref = this.link;
      }
    },
    async openLink() {
      const data = await ProductApi.fetchProductDocuments(this.assetLocation, escape(this.link));
      if (!data) throw Error(`Bad document link: ${data}`);
      this.dynamicHref = data;
      this.externalLink = this.dynamicHref;
    },
    async onLinkButtonClick() {
      await this.processLink();
      if (this.hasExternalLinkAtBreakpoint) {
        this.isDialogVisible = true;
      } else {
        window.open(this.dynamicHref, this.linkTarget);
      }
    },
    async openGptReportLink() {
      const url = `/scorecard/processGptwLink?assetLocation=${this.assetLocation}&linkUrl=${encodeURIComponent(
        this.link
      )}`;
      const {data} = await storefrontInstance.get(url).catch((error) => {
        console.error(`Document link generation error: ${error}`);
      });
      if (data?.data) {
        this.dynamicHref = data.data;
      }
    },
    openCloudinaryLink() {
      const baseUrl = '//media.trekbikes.com/image/upload/';
      const transformations = this.isContent ? '' : 'w_160,h_120,f_auto,q_auto,fl_strip_profile/';
      const url = `${baseUrl}${transformations}${this.link}`;
      this.dynamicHref = url;
    },
    closeDialog() {
      this.isDialogVisible = false;
    },
  },
};
</script>
